import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
} from '@coreui/react-pro'
import ReactQuill from 'react-quill'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone, {
  MAX_IMAGE_SIZE,
  SUPPORTED_IMAGE_FORMATS,
} from 'src/components/common/ImageDropzone'

const AddForm = ({ visible, onClose, gradeList, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)
  const quillRef = useRef()
  const [ownerDetail, setOwnerDetail] = useState('')

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Team name is required').max(50, '50 Character Limit is allowed.'),
    grade_name: Yup.string().required('Comp is required'),
    image: Yup.mixed()
      .nullable()
      .test('fileFormat', 'Invalid file format, only JPG, PNG or WEBP is allowed', (value) =>
        value ? SUPPORTED_IMAGE_FORMATS.includes(value.type) : true,
      )
      .test('fileSize', 'File size too large, max file size is 2 MB', (value) =>
        value ? value.size <= MAX_IMAGE_SIZE : true,
      ),
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      grade_name: '',
      sponsor_link: '',
      image: null,
      team_owner: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('grade_name', data.grade_name)
      formData.append('sponsor_link', data.sponsor_link)
      formData.append('team_owner', data.team_owner)
      formData.append('owner_detail', ownerDetail)
      if (data.image) {
        formData.append('image', data.image)
      }

      setLoader(true)
      TeamService.saveTeam(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            actions.resetForm()
            setOwnerDetail('')
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    setErrorMessages([])
    setOwnerDetail('')
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 640 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Team</COffcanvasTitle>
          <div className="text-medium-emphasis small">Create a new team record.</div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <h6 className="fw-bold mb-0">Required Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="name">Team Name *</CFormLabel>
            <CFormInput
              placeholder="Team Name"
              className={
                'form-control' + (formik.errors.name && formik.touched.name ? ' is-invalid' : '')
              }
              value={formik.values.name}
              onChange={formik.handleChange}
              aria-label="name"
              id="name"
            />
            {formik.errors.name && formik.touched.name && (
              <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="grade_name">Comp This Team Plays In *</CFormLabel>
            <CFormSelect
              aria-label="Select Comp"
              name="grade_name"
              className={
                'form-control' +
                (formik.errors.grade_name && formik.touched.grade_name ? ' is-invalid' : '')
              }
              value={formik.values.grade_name}
              onChange={formik.handleChange}
              id="grade_name"
            >
              <option value="">Select Comp</option>
              {gradeList &&
                gradeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.grade} ({item?.sn})
                  </option>
                ))}
            </CFormSelect>
            {formik.errors.grade_name && formik.touched.grade_name && (
              <CFormFeedback invalid>{formik.errors.grade_name}</CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="sponsor_link">Team Sponsor Link</CFormLabel>
            <CFormInput
              placeholder="Team Sponsor Link"
              value={formik.values.sponsor_link}
              onChange={formik.handleChange}
              aria-label="sponsor_link"
              id="sponsor_link"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="team_owner">Team Owner</CFormLabel>
            <CFormInput
              placeholder="Team Owner"
              value={formik.values.team_owner}
              onChange={formik.handleChange}
              aria-label="team_owner"
              id="team_owner"
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Team Sponsor Logo</CFormLabel>
            <ImageDropzone
              minHeight={120}
              file={formik.values.image}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Owner Details</CFormLabel>
            <ReactQuill theme="snow" ref={quillRef} value={ownerDetail} onChange={setOwnerDetail} />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Team
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
